import React, {CSSProperties, useRef} from 'react';
import DrawControl from "components/DrawControl";
import * as Mapbox from "react-map-gl/mapbox";
import {FeatureCollection, Geometry} from "geojson";
import styles from "./styles";
import {PlusCircleIcon} from "@heroicons/react/outline";
import Map from "../../Map";
import {useOperator} from "context/OperatorContext";
import bbox from "@turf/bbox";
import {LngLatBoundsLike} from "mapbox-gl";
import {feature, featureCollection} from "../../../services/geometry";
import MapboxDraw, {DrawEvent, DrawModeChangeEvent} from "@mapbox/mapbox-gl-draw";

// Constants ----

const BOUNDS_PADDING = 40;

// Types ----

type LocationTypes = 'position' | 'area' | 'view'

type LocatorProps = {
  height: CSSProperties['height'],
  type: LocationTypes
  geometry?: Geometry
  flyToGeometry?: Geometry
  onChange?: (geometry?: Geometry) => any
  onViewChange?: (state: Mapbox.ViewState) => any
  buildingId?: number | null
  floorId?: number | null
  showSearchBox?: boolean
}

// Helpers ----

function calculateViewState(geometry?: Geometry): Mapbox.MapProps['initialViewState'] | null {
  if (!geometry) {
    return null;
  }

  return {
    bounds: bbox(geometry) as LngLatBoundsLike,
    fitBoundsOptions: {
      padding: { top: BOUNDS_PADDING, right: BOUNDS_PADDING, bottom: BOUNDS_PADDING, left: BOUNDS_PADDING }
    }
  }
}

function geometryFeatures(geometry: Geometry): FeatureCollection {
  return featureCollection([
    feature(Math.random(), {}, geometry)
  ])
}

// Component ----

function Locator({height = '32rem', type, geometry, onChange, onViewChange, buildingId, floorId, flyToGeometry, showSearchBox}: LocatorProps) {
  const {settings} = useOperator();

  // Keep a reference to the drawing control
  const drawControl = useRef<MapboxDraw | null>(null);

  // Prepare the initial view state
  const startingViewState = calculateViewState(geometry) ?? settings?.audience_app.map_state ?? null;

  // Prepare the features
  const editableFeatures = geometry ? geometryFeatures(geometry) : featureCollection([]);

  function handleLoaded(_event: object, control: MapboxDraw) {
    drawControl.current = control;
    control.set(editableFeatures);
  }

  function handleViewChanged(event: Mapbox.ViewStateChangeEvent) {
    if(onViewChange) onViewChange(event.viewState)
  }

  function handleModeChanged(event: DrawModeChangeEvent, control: MapboxDraw) {
    const drawingToolSelected = event.mode.startsWith('draw_');
    const featuresExist = control.getAll().features.length > 1;

    if (drawingToolSelected && featuresExist) {
      control.set(featureCollection(control.getAll().features.slice(-1)))
    }
  }

  function handleFeaturesChanged(_event: DrawEvent, control: MapboxDraw) {
    if(onChange) onChange(control.getAll().features[0]?.geometry)
  }

  return (
    <div className="relative rounded-md shadow-sm overflow-hidden">
      <Map
        height={height}
        defaultMapStyle={settings?.audience_app.map_style ?? null}
        initialViewState={startingViewState}
        onViewChanged={handleViewChanged}
        hidePointsOfInterest
        buildingId={buildingId}
        floorId={floorId}
        flyToGeometry={flyToGeometry}
        flyToMode='bounds'
        showSearchBox={showSearchBox}
      >
        <DrawControl
          controls={{polygon: type === 'area', point: type === 'position'}}
          styles={styles}
          displayControlsDefault={false}
          position="top-left"
          defaultMode={'simple_select'}
          onLoad={handleLoaded}
          onModeChange={handleModeChanged}
          onCreate={handleFeaturesChanged}
          onUpdate={handleFeaturesChanged}
          onDelete={handleFeaturesChanged}
        />
      </Map>

      {type === 'view' &&
        <div className="absolute top-0 left-0 h-full w-full flex items-center justify-center pointer-events-none">
          <div className="h-8 w-8 bg-white bg-opacity-50 rounded-full flex items-center justify-center">
            <PlusCircleIcon className="h-6 w-6 text-gray-800"/>
          </div>
        </div>
      }
    </div>
  );
}

export default Locator;